/** Support modal — lets users file bug reports or feature requests directly
 *  to the ContractGroup/Quantis GitHub repository from inside the app.
 *  Kept deliberately simple: most reporters aren't technical, so we ask only
 *  for a short description and an optional screenshot. Diagnostics (recent
 *  actions, failed network requests, console logs, app-state snapshot) are
 *  collected silently so the team can still diagnose without DevTools access.
 *
 *  Deliberately a NON-MODAL right-hand sheet, not a centred dialog: a bug
 *  report is written *about* the page behind it, so the reporter has to be
 *  able to keep clicking around and reproduce the fault while the form stays
 *  open. Outside clicks therefore do NOT dismiss it (that would close the
 *  panel the moment you touched the thing you were describing) — the X and
 *  Escape are the ways out. */

import { useState, useCallback } from 'react';
import { Bug, Lightbulb, ChevronRight, ChevronLeft, Maximize2, Loader2, ExternalLink, AlertCircle, CheckCircle2, Camera, Trash2, ImageIcon, Sparkles, MessageSquareText } from '@/shared/ui/icons';
import {
  InspectorSheet,
  InspectorPanel,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../shared/ui/sheet';
import { Button } from '../../shared/ui/button';
import { Input } from '../../shared/ui/input';
import { Label } from '../../shared/ui/label';
import { Textarea } from '../../shared/ui/textarea';
import { getAuthHeaders, API_BASE } from '../../utils/supabase/client';
import { getClientLogs, clientLogsAsText } from '../../utils/clientLogger';
import { getBreadcrumbs, breadcrumbsAsText } from '../../utils/breadcrumbs';
import { getNetworkLog, networkLogAsText } from '../../utils/networkRecorder';
import { captureAppState, appStateAsMarkdown, type AppStateSnapshot } from '../../utils/appStateSnapshot';
import { captureScreenshot, ScreenshotCancelled, type ScreenshotResult } from '../../utils/captureScreenshot';
import { OPEN_WHATS_NEW_EVENT } from '../../platform/realtime/WhatsNewSheet';
import { WHATS_NEW_ENABLED } from '../../release-notes';
import { uploadBugAttachments } from './uploadBugAttachments';

type ReportType = 'bug' | 'feature' | null;
type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

interface SupportModalProps {
  open: boolean;
  onClose: () => void;
  embedded?: boolean;
}

export function SupportModal({ open, onClose, embedded = false }: SupportModalProps) {
  const [reportType, setReportType] = useState<ReportType>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // Bug-report fields, pared back to the essentials a non-technical user can
  // answer without guidance.
  const [whatsWrong, setWhatsWrong] = useState('');
  const [whatHappened, setWhatHappened] = useState('');
  const [screenshot, setScreenshot] = useState<ScreenshotResult | null>(null);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);

  /**
   * Hand the PNG to the platform's own image viewer via a blob URL: real zoom,
   * pan and Save, at native resolution, with no viewer code of our own. A
   * data: URL cannot be used — Chrome blocks top-level navigation to those.
   */
  const openScreenshotNatively = useCallback(() => {
    if (!screenshot) return;
    const url = URL.createObjectURL(screenshot.blob);
    const win = window.open(url, '_blank', 'noopener');
    if (!win) {
      // Popup-blocked, or a runtime with no window.open (the WKWebView shells).
      // Say so rather than leaving the click looking broken.
      URL.revokeObjectURL(url);
      setErrorMessage('Could not open the screenshot. Allow pop-ups for this site to view it full size.');
      return;
    }
    // Revoking immediately would kill the image the new tab is still loading.
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }, [screenshot]);
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [issueUrl, setIssueUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [screenshotDropped, setScreenshotDropped] = useState(false);

  const resetForm = useCallback(() => {
    setReportType(null);
    setTitle('');
    setDescription('');
    setWhatsWrong('');
    setWhatHappened('');
    setScreenshot(null);
    setCapturingScreenshot(false);
    setSubmitState('idle');
    setIssueUrl(null);
    setErrorMessage('');
    setScreenshotDropped(false);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleCaptureScreenshot = async () => {
    setCapturingScreenshot(true);
    setErrorMessage('');
    // Panel hiding is handed to the capture helper rather than done here: the
    // Screen Capture API opens its picker first, and the stream is live, so the
    // panel must come out only once the user has chosen a surface. Hiding it up
    // front would just mean it is missing while they pick.
    const panelEl = document.querySelector<HTMLElement>('[data-slot="support-panel"]');
    let prevDisplay = '';
    try {
      const result = await captureScreenshot({
        beforeGrab: () => {
          if (!panelEl) return;
          prevDisplay = panelEl.style.display;
          panelEl.style.display = 'none';
        },
        afterGrab: () => {
          if (panelEl) panelEl.style.display = prevDisplay;
        },
      });
      setScreenshot(result);
    } catch (err) {
      // Dismissing the picker is a decision, not a failure — say nothing.
      if (err instanceof ScreenshotCancelled) return;
      setErrorMessage(err instanceof Error ? err.message : 'Could not capture screenshot');
    } finally {
      setCapturingScreenshot(false);
    }
  };

  const handleSubmit = async () => {
    if (!reportType || !canSubmit) return;

    setSubmitState('submitting');
    setErrorMessage('');

    try {
      const headers = await getAuthHeaders();

      const isBug = reportType === 'bug';
      // Diagnostics are always collected for bug reports — they're the most
      // valuable signal and the reporter shouldn't have to think about them.
      const consoleLogs = isBug
        ? clientLogsAsText(
            getClientLogs()
              .filter(e => e.level === 'warn' || e.level === 'error')
              .slice(-30)
          )
        : '';
      const breadcrumbs = isBug ? breadcrumbsAsText(getBreadcrumbs()) : '';
      const networkLog = isBug ? networkLogAsText(getNetworkLog()) : '';
      const appState: AppStateSnapshot | null = isBug ? captureAppState() : null;

      // Upload screenshot + diagnostics first so we have URLs to embed
      let screenshotUrl: string | undefined;
      let diagnosticsUrl: string | undefined;

      if (isBug && (screenshot || breadcrumbs || networkLog || consoleLogs)) {
        const form = new FormData();
        if (screenshot) form.append('screenshot', screenshot.blob, 'screenshot.png');

        // Build a full diagnostics JSON for the attachment — same data as embedded,
        // but uncondensed and with the full URLs / response snippets preserved.
        const diagnostics = {
          capturedAt: new Date().toISOString(),
          appState,
          breadcrumbs: getBreadcrumbs(),
          network: getNetworkLog(),
          consoleLogs: getClientLogs().filter(e => e.level === 'warn' || e.level === 'error').slice(-100),
        };
        const diagnosticsBlob = new Blob([JSON.stringify(diagnostics, null, 2)], {
          type: 'application/json',
        });
        form.append('diagnostics', diagnosticsBlob, 'diagnostics.json');

        const uploaded = await uploadBugAttachments(`${API_BASE}/github/bug-attachment`, headers, form);
        screenshotUrl = uploaded.screenshotUrl;
        diagnosticsUrl = uploaded.diagnosticsUrl;
        // The upload never throws, so a missing URL is the only sign the
        // screenshot was lost; the report still goes ahead without it.
        setScreenshotDropped(Boolean(screenshot) && !uploaded.screenshotUrl);
      }

      const body = buildIssueBody({
        reportType,
        description,
        bug: {
          whatsWrong: whatsWrong.trim(),
          whatHappened: whatHappened.trim(),
        },
        consoleLogs,
        breadcrumbs,
        networkLog,
        appState,
        screenshotUrl,
        diagnosticsUrl,
      });

      const primaryDescription = isBug ? whatsWrong.trim() : description.trim();

      const res = await fetch(`${API_BASE}/github/issues`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          body,
          type: reportType,
          description: primaryDescription,
          consoleLogs: consoleLogs || undefined,
          pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error ?? `Request failed (${res.status})`);
      }

      const data = await res.json() as { html_url?: string };
      setIssueUrl(data.html_url ?? null);
      setSubmitState('success');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setSubmitState('error');
    }
  };

  const canSubmit = reportType === 'bug'
    ? title.trim().length > 0 && whatsWrong.trim().length > 5
    : reportType === 'feature'
      ? title.trim().length > 0 && description.trim().length > 5
      : false;

  const panelBody = (
    <>
        {/* Visually removed: on open the two cards below already say what
            this panel is, and once a type is chosen the selected card says
            which mode you are in — a title band restating it just pushed the
            choice down the panel. Kept in the tree and sr-only because Radix
            requires a Title on dialog content and screen readers need it; a
            bare removal trips a console warning and leaves the panel unnamed. */}
        {embedded ? (
          <h2 className="sr-only">Support</h2>
        ) : (
          <SheetHeader className="sr-only">
            <SheetTitle>
              {submitState === 'success'
                ? 'Report sent'
                : reportType === 'bug'
                  ? 'Report a problem'
                  : reportType === 'feature'
                    ? 'Suggest an idea'
                    : 'Send feedback'}
            </SheetTitle>
            <SheetDescription>
              {submitState === 'success'
                ? 'Thanks, your report has reached the team.'
                : 'Report a problem or suggest an idea.'}
            </SheetDescription>
          </SheetHeader>
        )}

        {/* Success state */}
        {submitState === 'success' && (
          <div className="flex flex-col items-center gap-4 px-5 pb-5 pt-2">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30">
              <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <p className="text-sm text-center text-muted-foreground">
              {reportType === 'bug'
                ? 'The team will take a look and get this sorted.'
                : 'The team will review your idea for a future update.'}
            </p>
            {screenshotDropped && (
              <p role="status" className="text-sm text-center text-amber-700 dark:text-amber-400">
                Your screenshot could not be attached, so the report went without it. Describe what you saw if it matters, or send it to the team directly.
              </p>
            )}
            {issueUrl && (
              <a
                href={issueUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                View on GitHub
              </a>
            )}
            <Button className="mt-2" onClick={handleClose}>Done</Button>
          </div>
        )}

        {/* Form */}
        {submitState !== 'success' && (
          <>
            {/* flex-1 min-h-0 is what pins the footer to the floor. Without it
                this body sizes to its content and the footer sits directly
                underneath, stranding Send mid-panel with dead space below —
                harmless in the old max-h-[85vh] dialog that always overflowed,
                obvious in a full-height sheet. min-h-0 lets it shrink so
                overflow-y-auto still scrolls rather than pushing the footer off. */}
            <div className="flex flex-1 min-h-0 flex-col gap-3 overflow-y-auto px-5 pt-4 pb-1">
              {/* Step 1 of 2. The chooser is a page of its own: showing the
                  cards above a live form put a decision and its consequence on
                  screen at once and made a 420px panel feel like a wall. Picking
                  one swaps this out for the form. */}
              {!reportType && (
              <div className="flex flex-col gap-2.5 pt-1">
                <TypeCard
                  icon={<Bug className="w-[18px] h-[18px]" />}
                  label="Something's wrong"
                  sublabel="Report a problem"
                  selected={reportType === 'bug'}
                  onSelect={() => setReportType('bug')}
                />
                <TypeCard
                  icon={<Lightbulb className="w-[18px] h-[18px]" />}
                  label="I have an idea"
                  sublabel="Suggest an improvement"
                  selected={reportType === 'feature'}
                  onSelect={() => setReportType('feature')}
                />
              </div>
              )}

              {/* What's New — only on the landing screen, before a report type
                  is chosen. Closes this modal and opens the release-notes
                  panel via the shared window event. Hidden while the feature
                  is parked (WHATS_NEW_ENABLED = false): the sheet never
                  mounts then, so the event would open nothing. */}
              {!reportType && WHATS_NEW_ENABLED && (
                <button
                  type="button"
                  onClick={() => {
                    handleClose();
                    // The release-notes panel is itself a modal Radix dialog.
                    // Opening it in the same tick this one closes leaves two
                    // scroll-locks racing, which strands the body on
                    // pointer-events:none and makes the new sheet unclickable.
                    // Wait out this dialog's 200ms close animation so only one
                    // modal layer is ever mounted at once.
                    window.setTimeout(() => {
                      window.dispatchEvent(new Event(OPEN_WHATS_NEW_EVENT));
                    }, 220);
                  }}
                  className="flex items-center gap-2 rounded-lg px-1 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Sparkles className="w-3.5 h-3.5 text-primary" />
                  See what's new
                  <ChevronRight className="w-3.5 h-3.5 ml-auto" />
                </button>
              )}

              {/* Step 2 of 2. */}
              {reportType && (
                <>
                  {/* Back to the chooser. A leading chevron per the Cancel vs
                      Close rule: this steps back a page, it does not discard or
                      dismiss — Cancel in the footer still does that. */}
                  <button
                    type="button"
                    onClick={() => setReportType(null)}
                    className="-ml-1 -mt-1 flex items-center gap-1 self-start rounded-lg px-1 py-1 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    {reportType === 'bug' ? "Something's wrong" : 'I have an idea'}
                  </button>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="support-title">Title</Label>
                    <Input
                      id="support-title"
                      placeholder={reportType === 'bug'
                        ? 'e.g. Can\'t save my changes'
                        : 'e.g. Let me duplicate a project'}
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      maxLength={140}
                    />
                  </div>

                  {reportType === 'feature' && (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="support-description">Describe your idea</Label>
                      <Textarea
                        id="support-description"
                        placeholder="What would you like to be able to do?"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        rows={4}
                        className="resize-none"
                      />
                    </div>
                  )}

                  {reportType === 'bug' && (
                    <>
                      <BugField
                        id="bug-whats-wrong"
                        label="What went wrong?"
                        required
                        placeholder="Describe the problem in your own words"
                        value={whatsWrong}
                        onChange={setWhatsWrong}
                        rows={4}
                      />
                      <BugField
                        id="bug-what-happened"
                        label="What were you doing?"
                        placeholder="Optional: what you were doing when it happened"
                        value={whatHappened}
                        onChange={setWhatHappened}
                      />

                      {/* Screenshot capture. Laid out like every other field in
                          this form: a label row above its control, no wrapper.
                          It used to be a bordered, tinted sub-card holding a
                          separately bordered thumbnail, which framed one thing
                          twice and made it the only boxed outline left in the
                          panel. The thumbnail now carries the surface itself,
                          on the same shadow the choice cards use. */}
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center justify-between">
                          <Label className="font-normal flex items-center gap-1.5">
                            <ImageIcon className="w-3.5 h-3.5" />
                            Screenshot
                            <span className="text-xs text-muted-foreground">(optional)</span>
                          </Label>
                          {screenshot ? (
                            <button
                              type="button"
                              onClick={() => setScreenshot(null)}
                              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
                            >
                              <Trash2 className="w-3 h-3" />
                              Remove
                            </button>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={handleCaptureScreenshot}
                              disabled={capturingScreenshot}
                              className="h-7 text-xs"
                            >
                              {capturingScreenshot ? (
                                <>
                                  <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                                  Capturing
                                </>
                              ) : (
                                <>
                                  <Camera className="mr-1.5 h-3 w-3" />
                                  Capture
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                        {screenshot ? (
                          <button
                            type="button"
                            onClick={openScreenshotNatively}
                            aria-label="View screenshot full size"
                            className="group relative self-start cursor-zoom-in overflow-hidden rounded-[var(--radius-widget)] transition-shadow shadow-[0_1px_3px_rgba(0,0,0,0.05),0_12px_30px_-18px_rgba(0,0,0,0.12)] hover:shadow-[0_2px_6px_rgba(0,0,0,0.07),0_16px_36px_-18px_rgba(0,0,0,0.18)]"
                          >
                            <img
                              src={screenshot.dataUrl}
                              alt="Captured screenshot preview"
                              className="bg-background max-h-40 max-w-full w-auto object-contain"
                            />
                            {/* At 420px the thumbnail is far too small to check
                                what was actually captured before sending. */}
                            <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all group-hover:bg-black/35 group-hover:opacity-100">
                              <Maximize2 className="h-5 w-5 text-white" />
                            </span>
                          </button>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            A picture of the screen really helps. The Support window is hidden while it's taken.
                          </p>
                        )}
                      </div>

                    </>
                  )}
                </>
              )}

              {submitState === 'error' && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  {errorMessage}
                </div>
              )}
            </div>

            {/* Fixed footer */}
            <div className="flex justify-end gap-2 border-t border-border/40 px-5 py-4">
              {/* Cancel discards edits and pairs with Send. On the landing
                  screen nothing has been entered yet, so the sheet's X is the
                  only dismissal needed. */}
              {reportType && (
                <Button variant="ghost" onClick={handleClose} disabled={submitState === 'submitting'}>
                  Cancel
                </Button>
              )}
              {reportType && (
                <Button
                  onClick={handleSubmit}
                  disabled={!canSubmit || submitState === 'submitting'}
                >
                  {submitState === 'submitting' ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </>
        )}
    </>
  );

  if (embedded) {
    return open ? (
      <section data-slot="support-panel" aria-label="Support" className="flex h-full min-h-0 w-full flex-col overflow-hidden">
        {panelBody}
      </section>
    ) : null;
  }

  return (
    <InspectorSheet open={open} modal={false} onOpenChange={nextOpen => { if (!nextOpen) handleClose(); }}>
      <InspectorPanel
        hideCloseButton
        data-slot="support-panel"
        className="w-[95%] sm:max-w-[420px] flex flex-col gap-0 p-0 overflow-hidden"
        onInteractOutside={e => e.preventDefault()}
        onPointerDownOutside={e => e.preventDefault()}
      >
        {panelBody}
      </InspectorPanel>
    </InspectorSheet>
  );
}

interface TypeCardProps {
  icon: React.ReactNode;
  label: string;
  sublabel: string;
  selected: boolean;
  onSelect: () => void;
}

/** One of the two full-width choice cards. Matches the Home dashboard's card
 *  language: borderless, defined by a soft shadow rather than a hairline frame
 *  (DashboardCard's own note is that a frame "reads as too carded"), with the
 *  selected state shown by the same 3px left tone rail those widgets use.
 *  Neither is selected on open, which is what gates the fields below. */
function TypeCard({ icon, label, sublabel, selected, onSelect }: TypeCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={[
        'relative flex w-full items-center gap-3 overflow-hidden p-4 text-left',
        'rounded-[var(--radius-widget)] bg-card transition-shadow',
        // Same two-layer shadow DashboardCard uses, so these sit on the panel
        // the way the widgets sit on the canvas.
        'shadow-[0_1px_3px_rgba(0,0,0,0.05),0_12px_30px_-18px_rgba(0,0,0,0.12)]',
        'hover:shadow-[0_2px_6px_rgba(0,0,0,0.07),0_16px_36px_-18px_rgba(0,0,0,0.18)]',
        selected
          ? 'before:absolute before:inset-y-3 before:left-0 before:w-[3px] before:rounded-r-full before:bg-brand'
          : '',
      ].join(' ')}
    >
      <span
        className={[
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors',
          selected
            ? 'bg-primary text-white'
            : 'bg-primary/12 dark:bg-primary/20 text-primary',
        ].join(' ')}
      >
        {icon}
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[15px] font-medium leading-tight">{label}</span>
        <span className="text-[13px] text-muted-foreground leading-tight">{sublabel}</span>
      </span>
    </button>
  );
}

interface BugFieldProps {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  rows?: number;
}

function BugField({ id, label, placeholder, value, onChange, required, rows = 3 }: BugFieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      <Textarea
        id={id}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={rows}
        className="resize-none"
      />
    </div>
  );
}

/** Formats the GitHub issue body in Markdown. */
function buildIssueBody(opts: {
  reportType: 'bug' | 'feature';
  description: string;
  bug: {
    whatsWrong: string;
    whatHappened: string;
  };
  consoleLogs: string;
  breadcrumbs: string;
  networkLog: string;
  appState: AppStateSnapshot | null;
  screenshotUrl?: string;
  diagnosticsUrl?: string;
}): string {
  const { reportType, description, bug, consoleLogs, breadcrumbs, networkLog, appState, screenshotUrl, diagnosticsUrl } = opts;
  const url = typeof window !== 'undefined' ? window.location.href : 'n/a';
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a';
  const buildInfo = (globalThis as unknown as { __APP_BUILD_INFO__?: { buildId?: string } }).__APP_BUILD_INFO__;
  const buildId = buildInfo?.buildId ?? 'n/a';

  if (reportType === 'feature') {
    return [
      '## Feature request',
      '',
      description,
      '',
      '---',
      `**Submitted from:** ${url}`,
      `**Build:** ${buildId}`,
    ].join('\n');
  }

  const sections: string[] = [
    '## Bug Report',
    '',
    '### What went wrong?',
    bug.whatsWrong,
  ];

  if (bug.whatHappened) {
    sections.push('', '### What were you doing?', bug.whatHappened);
  }

  if (screenshotUrl) {
    sections.push('', '### Screenshot', `![Screenshot](${screenshotUrl})`);
  }

  sections.push('', '### Environment');
  if (appState) {
    sections.push(appStateAsMarkdown(appState));
    sections.push('', `**Browser:** ${ua}`);
  } else {
    sections.push(
      `- **URL:** ${url}`,
      `- **Build:** ${buildId}`,
      `- **Browser:** ${ua}`,
    );
  }

  if (breadcrumbs) {
    sections.push(
      '',
      '<details><summary>User actions (last 50)</summary>',
      '',
      '```',
      breadcrumbs,
      '```',
      '</details>',
    );
  }

  if (networkLog) {
    sections.push(
      '',
      '<details><summary>Network requests (last 30)</summary>',
      '',
      '```',
      networkLog,
      '```',
      '</details>',
    );
  }

  if (consoleLogs) {
    sections.push(
      '',
      '<details><summary>Console warnings & errors</summary>',
      '',
      '```',
      consoleLogs,
      '```',
      '</details>',
    );
  }

  if (diagnosticsUrl) {
    sections.push('', `[Full diagnostics JSON](${diagnosticsUrl})`);
  }

  return sections.join('\n');
}
